import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useCurrentOrganization } from 'src/store/organization';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { BreadcrumbsConfig, MetricCard } from '../constants';
import { ContentInsight } from '../content-insight.interface';
import useDashboardActiveTab from 'src/dashboard/helpers/active-tab.hook';

import {
  createContentTotalViewsCard,
  createContentAverageViewTimeCard,
  createContentUsedInJourneysCard,
  createContentClickThroughRateCard,
} from '../helpers/metric-cards';
import { DashboardInsightsItemLayout } from '../item-layout';
import { apiGetContentDocumentInsights, apiGetContentInsights } from 'src/utils/journeyApi';
import { DashboardInsightsContentJourneysTable } from './journeys-table';
import { Node } from 'src/common/interfaces/node.interface';
import { DashboardInsightsJourneysJourneyPdfViewsChart } from '../journeys/journey/pdf-views-chart';
import { DocumentInsight } from '../journeys/constants';
import { Helmet } from 'react-helmet';
import { ContentCardWithPreview } from 'src/common/components/content/card-with-preview';
import { ErrorInterface } from 'src/common/interfaces/error.interface';

type DashboardInsightsContentProps = {
  contentUuid: string;
};

type ContentDocumentInsights = {
  insights: DocumentInsight[];
  node: Node;
};

export const DashboardInsightsContent: FC<DashboardInsightsContentProps> = ({ contentUuid }) => {
  useDashboardActiveTab('insights');

  const history = useHistory();
  const { currentOrganization } = useCurrentOrganization();
  const [loading, setLoading] = useState(true);
  const [contentInsights, setContentInsights] = useState<ContentInsight>();
  const [contentDocumentInsights, setContentDocumentInsights] = useState<ContentDocumentInsights>();

  const fetchContentInsights = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiGetContentInsights(currentOrganization.id, {
        content_uuid: contentUuid,
      });
      if (response.contents.length > 0) {
        setContentInsights(response.contents[0]);
      }
      const documentInsightsResponse = await apiGetContentDocumentInsights(currentOrganization.id, contentUuid);
      setContentDocumentInsights(documentInsightsResponse);
    } catch (err) {
      if (contentUuid && (err as ErrorInterface).message.startsWith('Not a valid uuid')) {
        history.replace(`/${currentOrganization.slug}/insights/content`);
      }
    } finally {
      setLoading(false);
    }
  }, [currentOrganization.id, contentUuid]);

  useEffect(() => {
    fetchContentInsights();
  }, [contentUuid, fetchContentInsights]);

  const currentContent = useMemo(() => {
    return contentInsights?.content;
  }, [contentInsights]);

  const breadcrumbsConfig: BreadcrumbsConfig = {
    items: [
      {
        value: 'Insights',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights`);
        },
      },
      {
        value: 'Content',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights/content`);
        },
      },
      { value: currentContent?.name || '' },
    ],
  };

  if (loading) {
    return <FixedLoadingSpinner />;
  }

  const metricCards: MetricCard[] = contentInsights
    ? [
        createContentUsedInJourneysCard(contentInsights),
        createContentTotalViewsCard(contentInsights),
        createContentAverageViewTimeCard(contentInsights),
        createContentClickThroughRateCard(contentInsights),
      ]
    : [];

  return (
    <>
      <Helmet>
        <title>Journey | Insights | Content | {currentContent?.name}</title>
      </Helmet>
      <DashboardInsightsItemLayout
        metricCards={metricCards}
        headerSection={
          <ContentCardWithPreview
            alignment='center'
            placement='bottom'
            containerClassName='group cursor-default items-center'
            content={currentContent}
          >
            {(name) => <h1 className='text-bedrock-serif-h1 ml-4 line-clamp-2 lg:line-clamp-none'>{name}</h1>}
          </ContentCardWithPreview>
        }
        breadcrumbsConfig={breadcrumbsConfig}
        contentSection={
          <div className='flex flex-col space-y-4'>
            {contentDocumentInsights && contentDocumentInsights.insights && contentDocumentInsights.node && (
              <div className='flex'>
                <DashboardInsightsJourneysJourneyPdfViewsChart
                  node={contentDocumentInsights.node}
                  documentInsights={contentDocumentInsights.insights}
                  chartOptions={{ yLabel: 'Total time in seconds' }}
                />
              </div>
            )}
            <DashboardInsightsContentJourneysTable contentInsight={contentInsights} />
          </div>
        }
      />
    </>
  );
};
